'use client';

import type React from 'react';

import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Project } from '@/app/models/project';
import type { ProjectPayload, ProjectRequest } from '@/app/models/providersResponse';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useAddProjectsMutation } from '@/app/services/providerApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalLabel from '@/components/common/ModalLabel';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import TextButton from '@/components/common/TextButton';
import { usePeriod } from '@/context/PeriodContext';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import ProjectTable from './ProjectTable';

interface NewEditTeacherPaymentProps {
  periodRequest: string | null;
  setDrawerOpen: (open: boolean) => void;
  refetch: () => void;
}

export default function NewEditValueProvider(props: NewEditTeacherPaymentProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['provider', 'common']);
  const { selectedPeriod } = usePeriod();

  const { periodRequest, setDrawerOpen, refetch } = props;

  const [projects, setProjects] = useState<ProjectPayload[]>([]);
  const [inputValue, setInputValue] = useState('00,00');

  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery({
    searchTerm: originProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const handleAddProject = () => {
    const numericValue = Number(inputValue.replace(/\./g, '').replace(',', '.'));

    if (!selectedPeriod?.date || !originProject) return;

    const newProject: ProjectPayload = {
      extraValue: numericValue,
      period: selectedPeriod.date,
      projectName: originProject.name,
      projectCode: originProject.code,
      projectId: originProject.id,
    };

    setProjects((prevValue) => [...prevValue, newProject]);
    setInputValue('00,00');
    setOriginProject('' as unknown as Project);
    setOriginProjectSearch('');
  };

  const handleRemove = (removedProject: number) => {
    setProjects((prevValue) => prevValue.filter((project) => project.projectId !== removedProject));
  };

  const [authorizeMonthlyExpense] = useAddProjectsMutation();

  const handleSubmit = async () => {
    if (!projects || projects.length < 1) return;

    const requestBody: ProjectRequest = {
      extraValues: projects.map((project) => {
        return { extraValue: project.extraValue, providerId: project.projectId };
      }),
      period: selectedPeriod?.date as string,
    };

    await authorizeMonthlyExpense(requestBody);
    setDrawerOpen(false);
    toast.success(t('new.value.added'));
    refetch();
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const formatCurrency = (value: string) => {
    const isNegative = value.startsWith('-');
    const numericPart = value.replace(/\D/g, '');
    const floatValue = parseInt(numericPart, 10) / 100;

    const formattedNumber = floatValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return isNegative ? `-${formattedNumber}` : formattedNumber;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');

    const raw = hasNegative ? `-${cleaned}` : cleaned;

    setInputValue(formatCurrency(raw));
  };

  const alreadyIncluded = (value: Project) => {
    const filteredProjects = projects.filter((item) => item.projectCode === value.code);
    return filteredProjects.length >= 1;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('add.value.to.project')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: theme.spacing(4),
            gap: theme.spacing(2),
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', mb: theme.spacing(3) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {`${t('period')}:`}
              {selectedPeriod?.date && (
                <Typography component="span" sx={{ ml: theme.spacing(1), fontWeight: 'normal' }}>
                  {DateTime.fromISO(selectedPeriod.date).setLocale(i18n.language).toFormat('LLLL/yyyy')}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('value.info')}
            </Typography>
          </Box>

          <Box sx={{ display: 'block', minWidth: theme.spacing(31.25), mb: theme.spacing(4) }}>
            <ModalLabel title={`${t('project')}`} subtitle={t('project.info') as string} />
            <GenericAutoComplete
              value={originProject}
              onChange={(value) => {
                setOriginProject(value);
              }}
              equalityTest={(option, value) => option?.code === value?.code}
              getOptionLabel={(option) =>
                `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
              }
              onSearch={(value) => {
                setOriginProjectSearch(value);
              }}
              options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
              placeholder={t('enter.project.name.or.code') as string}
              loading={isLoadingOrigin}
              sx={{ width: '100%' }}
            />
          </Box>

          <Box sx={{ display: 'block', minWidth: theme.spacing(31.25) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('value.r$')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <TextField value={inputValue} onChange={handleChange} variant="outlined" size="small" />
              <TextButton
                text={`+ ${t('add')}`}
                disabled={!originProject || !inputValue || alreadyIncluded(originProject)}
                onClick={handleAddProject}
              />
            </Box>
          </Box>

          <Box sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }}>
            <ProjectTable projects={projects as ProjectPayload[]} onRemove={handleRemove} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={projects.length < 1} />
      </Box>
    </Box>
  );
}
